import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import WalletGauge from '../../components/wallet/WalletGauge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Wallet as WalletIcon,
  Plus,
  CreditCard,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useSearchParams } from 'react-router-dom';

const WalletPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [topupHours, setTopupHours] = useState('10');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWallet();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchWallet = async () => {
    try {
      const response = await api.get('/wallet/');
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Vos heures ont été ajoutées.');
        fetchWallet();
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleTopup = async () => {
    const hours = parseFloat(topupHours);
    if (!hours || hours <= 0) {
      toast.error('Veuillez entrer un nombre d\'heures valide');
      return;
    }
    
    setProcessing(true);
    
    try {
      const response = await api.post('/wallet/topup', {
        hours,
        payment_method: paymentMethod
      });
      
      if (paymentMethod === 'stripe') {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        // Bank transfer
        toast.success('Demande de virement enregistrée');
        setShowTopup(false);
        fetchWallet();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
      setProcessing(false);
    }
  };

  const hourlyRate = wallet?.hourly_rate || 75;
  const presetAmounts = [5, 10, 20, 50];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d]" data-testid="wallet-title">
              Mon Wallet
            </h1>
            <p className="text-[#64748b] mt-1">
              Gérez votre crédit d'heures
            </p>
          </div>
          
          <Dialog open={showTopup} onOpenChange={setShowTopup}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="topup-btn">
                <Plus className="w-4 h-4 mr-2" />
                Recharger
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Recharger mon wallet</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Quick amounts */}
                <div className="space-y-2">
                  <Label>Heures à créditer</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {presetAmounts.map((amount) => (
                      <Button
                        key={amount}
                        type="button"
                        variant={topupHours === String(amount) ? 'default' : 'outline'}
                        className={cn(
                          topupHours === String(amount) && 'bg-[#00d4aa] text-[#1a365d] hover:bg-[#00b38f]'
                        )}
                        onClick={() => setTopupHours(String(amount))}
                      >
                        {amount}h
                      </Button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    value={topupHours}
                    onChange={(e) => setTopupHours(e.target.value)}
                    placeholder="Nombre d'heures"
                    className="input-default mt-2"
                    data-testid="topup-hours-input"
                  />
                </div>
                
                {/* Amount preview */}
                <div className="p-4 bg-[#f8fafc] rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="text-[#64748b]">Montant total</span>
                    <span className="font-mono text-2xl font-bold text-[#1a365d]">
                      {(parseFloat(topupHours || 0) * hourlyRate).toLocaleString('fr-FR')}€
                    </span>
                  </div>
                  <p className="text-xs text-[#64748b] mt-1">
                    Taux horaire: {hourlyRate}€/h
                  </p>
                </div>
                
                {/* Payment method */}
                <div className="space-y-2">
                  <Label>Mode de paiement</Label>
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-[#f8fafc] cursor-pointer">
                      <RadioGroupItem value="stripe" id="stripe" />
                      <Label htmlFor="stripe" className="flex items-center gap-2 cursor-pointer flex-1">
                        <CreditCard className="w-5 h-5 text-[#6366f1]" />
                        <div>
                          <p className="font-medium">Carte bancaire</p>
                          <p className="text-xs text-[#64748b]">Paiement sécurisé par Stripe</p>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-[#f8fafc] cursor-pointer">
                      <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                      <Label htmlFor="bank_transfer" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Building2 className="w-5 h-5 text-[#1a365d]" />
                        <div>
                          <p className="font-medium">Virement bancaire</p>
                          <p className="text-xs text-[#64748b]">Délai de traitement: 2-3 jours</p>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <Button 
                  onClick={handleTopup}
                  className="btn-primary w-full h-12"
                  disabled={processing}
                  data-testid="confirm-topup-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Traitement...
                    </>
                  ) : paymentMethod === 'stripe' ? (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payer {(parseFloat(topupHours || 0) * hourlyRate).toLocaleString('fr-FR')}€
                    </>
                  ) : (
                    'Demander un virement'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Wallet Balance */}
            <Card className="card lg:col-span-1" data-testid="wallet-balance-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1a365d] flex items-center gap-2">
                  <WalletIcon className="w-5 h-5" />
                  Solde actuel
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center py-6">
                <WalletGauge 
                  balance={wallet?.balance_hours || 0}
                  maxBalance={100}
                  hourlyRate={hourlyRate}
                />
              </CardContent>
            </Card>

            {/* Transactions */}
            <Card className="card lg:col-span-2" data-testid="transactions-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1a365d]">
                  Historique des transactions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {wallet?.transactions?.length > 0 ? (
                    wallet.transactions.map((transaction, index) => (
                      <div 
                        key={transaction.id || index}
                        className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0]"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                          )}>
                            {transaction.type === 'credit' ? (
                              <ArrowDownRight className="w-5 h-5 text-green-600" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#1a365d]">
                              {transaction.description}
                            </p>
                            <p className="text-xs text-[#64748b]">
                              {new Date(transaction.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={cn(
                            "font-mono font-bold",
                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                          )}>
                            {transaction.type === 'credit' ? '+' : '-'}{transaction.hours}h
                          </p>
                          <p className="text-xs text-[#64748b]">
                            {transaction.amount?.toLocaleString('fr-FR')}€
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 text-[#64748b]">
                      <WalletIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune transaction pour le moment</p>
                      <Button 
                        onClick={() => setShowTopup(true)}
                        className="btn-primary mt-4"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Effectuer mon premier rechargement
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        <Card className="card mt-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#00d4aa]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <WalletIcon className="w-6 h-6 text-[#00d4aa]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1a365d] mb-2">
                  Comment fonctionne le wallet ?
                </h3>
                <p className="text-sm text-[#64748b]">
                  Votre wallet vous permet de provisionner des heures de communication valables pour tous les services de l'agence MSC.
                  Rechargez votre compte en heures et utilisez-les sur vos projets. Le taux horaire actuel est de <strong>{hourlyRate}€/h</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default WalletPage;
